import { useEffect, useState, type FormEvent } from "react";
import type { FxAccountOverview, FxAnnahmeVorschau, FxBalanceSnapshot, FxSnapshotInput } from "../../api/kurssicherung.ts";
import { todayIso } from "../../lib/format.ts";
import type { FxDeal } from "../../lib/fx.ts";
import { centAlsUsdText, liesDezimaleingabe, usdInCent } from "../../lib/dezimaleingabe.ts";
import { kontoMischkursVerfuegbar } from "../../lib/fxKontostand.ts";
import { sicherungsNr } from "../../lib/sicherungsNummer.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { FxDialog } from "./FxDialog.tsx";

type AnnahmeVorschauLaden = (accountId: string, input: Pick<FxSnapshotInput, "dealId" | "capturedOn">) => Promise<FxAnnahmeVorschau>;

export interface SicherungUebernehmenDialogProps {
  deal: FxDeal;
  konto: FxAccountOverview;
  /** Eindeutiger Anzeigename des Kontos (bei gleichnamigen Konten mit Zusatz); Vorgabe: Kontoname. */
  kontoName?: string;
  rechte: ReadonlySet<string>;
  snapshotAnlegen: (input: FxSnapshotInput) => Promise<FxBalanceSnapshot>;
  annahmeVorschauLaden: AnnahmeVorschauLaden;
  /** Nach erfolgreicher Uebernahme: Uebersicht neu laden. */
  onUebernommen: () => Promise<void> | void;
  onSchliessen: () => void;
}

/** Die fachlich massgebliche Rate bleibt Servertext; fuer die deutsche Anzeige wird nur das Trennzeichen lokalisiert. */
const dezimalAnzeige = (wert: string): string => wert.replace(".", ",");

/**
 * Dialog "Sicherung uebernehmen": die bestehende servergebundene Uebernahme (Vorschau vom Server,
 * erwarteter Stand muss centgenau stimmen) fuer genau die angeklickte Sicherung.
 */
export function SicherungUebernehmenDialog({ deal, konto, kontoName = konto.account.name, rechte, snapshotAnlegen, annahmeVorschauLaden, onUebernommen, onSchliessen }: SicherungUebernehmenDialogProps) {
  const [capturedOn, setCapturedOn] = useState(todayIso());
  const [erwartet, setErwartet] = useState("");
  const [vorschau, setVorschau] = useState<FxAnnahmeVorschau | null>(null);
  const [vorschauFehler, setVorschauFehler] = useState("");
  const [absendeFehler, setAbsendeFehler] = useState("");
  const [uebernimmt, setUebernimmt] = useState(false);
  const gesperrt = !hatRecht(rechte, "manage_fx");
  const aktuell = konto.current;
  const stichtagGueltig = Boolean(capturedOn) && (!aktuell || capturedOn >= aktuell.effectiveOn);
  // Als Cent verglichen, nicht als Text: "1234,5", "1.234,50" und "1234.50" sind derselbe Betrag.
  const lesung = liesDezimaleingabe(erwartet, 2);
  const erwartetFehler = erwartet !== "" && "fehler" in lesung ? lesung.fehler : "";
  const erwartetCent = "wert" in lesung ? usdInCent(lesung.wert) : null;
  const vorschauCent = vorschau ? usdInCent(vorschau.balanceUsd) : null;
  const stimmt = erwartetCent !== null && vorschauCent !== null && erwartetCent === vorschauCent;
  const kannUebernehmen = !gesperrt && !uebernimmt && !vorschauFehler && stichtagGueltig && vorschau !== null && stimmt;

  useEffect(() => {
    setVorschau(null);
    setVorschauFehler("");
    setAbsendeFehler("");
    setErwartet("");
    if (!stichtagGueltig) return;
    let aktiv = true;
    void annahmeVorschauLaden(konto.account.id, { dealId: String(deal.id), capturedOn })
      .then((antwort) => { if (aktiv) setVorschau(antwort); })
      .catch((ursache: unknown) => { if (aktiv) setVorschauFehler(fehlertext(ursache, "Die Annahmevorschau konnte nicht geladen werden.") ?? ""); });
    return () => { aktiv = false; };
  }, [annahmeVorschauLaden, capturedOn, deal.id, konto.account.id, stichtagGueltig]);

  async function uebernehmen(ereignis: FormEvent<HTMLFormElement>): Promise<void> {
    ereignis.preventDefault();
    if (!kannUebernehmen || erwartetCent === null) return;
    setUebernimmt(true);
    setAbsendeFehler("");
    try {
      await snapshotAnlegen({ accountId: konto.account.id, capturedOn, balanceUsd: centAlsUsdText(erwartetCent), dealId: String(deal.id) });
      await onUebernommen();
      onSchliessen();
    } catch (ursache) {
      setAbsendeFehler(fehlertext(ursache, "Die Sicherung konnte nicht übernommen werden.") ?? "");
    } finally {
      setUebernimmt(false);
    }
  }

  return (
    <FxDialog
      titelId="sicherungUebernehmenTitel"
      titel={`Sicherung ${sicherungsNr(deal)} übernehmen`}
      untertitel={<>{kontoName} · {String(deal.usd)} USD zum Kurs {String(deal.rate)}</>}
      beschaeftigt={uebernimmt}
      onSchliessen={onSchliessen}
      onAbsenden={(ereignis) => void uebernehmen(ereignis)}
      fuss={<><button className="btn-sec" type="button" disabled={uebernimmt} onClick={onSchliessen}>Abbrechen</button><button className="btn-primary" type="submit" disabled={!kannUebernehmen} title={gesperrt ? "fehlende Berechtigung" : undefined}>{uebernimmt ? "Übernimmt …" : "Ausgewählte Sicherung übernehmen"}</button></>}
    >
      <label>Übernahmestichtag<input aria-label="Übernahmestichtag" type="date" min={aktuell?.effectiveOn} value={capturedOn} disabled={gesperrt || uebernimmt} onChange={(ereignis) => setCapturedOn(ereignis.target.value)} /></label>
      <div className="fx-overview-preview" aria-live="polite">{vorschau ? <><span>Alter Kontostand</span><strong>{dezimalAnzeige(vorschau.previousBalanceUsd)} USD</strong><span>Alter Mischkurs</span><strong>{kontoMischkursVerfuegbar(vorschau.previousBalanceUsd) ? dezimalAnzeige(vorschau.previousMixedRate) : "nicht verfügbar"}</strong><span>Neue Sicherung</span><strong>{String(deal.usd)} USD</strong><span>Sicherungskurs</span><strong>{String(deal.rate)}</strong><span>Neuer Kontostand</span><strong>{dezimalAnzeige(vorschau.balanceUsd)} USD</strong><span>Neuer Mischkurs</span><strong>{dezimalAnzeige(vorschau.mixedRate)}</strong></> : <span>Vorschau wird geladen …</span>}</div>
      <label>Erwarteter Kontostand in USD<input aria-label="Erwarteter Kontostand in USD" inputMode="decimal" value={erwartet} disabled={gesperrt || uebernimmt} onChange={(ereignis) => setErwartet(ereignis.target.value)} /></label>
      {erwartetFehler && <p className="fx-overview-error" role="alert">Erwarteter Kontostand: {erwartetFehler}.</p>}
      {vorschau && erwartet !== "" && !erwartetFehler && !stimmt && <p className="fx-overview-error">Der Kontostand muss dem erwarteten Bestand exakt entsprechen.</p>}
      {capturedOn && !stichtagGueltig && <p className="fx-overview-error">Der Stichtag darf nicht vor dem Stichtag des aktuellen Kontostands liegen.</p>}
      {vorschauFehler && <p className="fx-overview-error" role="alert">{vorschauFehler}</p>}
      {absendeFehler && <p className="fx-overview-error" role="alert">{absendeFehler}</p>}
    </FxDialog>
  );
}
